/**
 * IndexedDB record types for the offline Dexie database.
 *
 * Every entity table includes three sync fields (via the SyncFields mixin):
 *   - sync_status:      'pending' | 'synced' | 'error'
 *   - version:          integer, incremented on each local mutation (optimistic lock)
 *   - local_updated_at: ISO string set on every local write
 *
 * `sync_queue` is excluded — it's the queue itself; its lifecycle uses the
 * dedicated `status` field defined on the SyncQueueRecord interface.
 *
 * Server columns mirror the backend SQLAlchemy models. Fields are `?` when the
 * server allows NULL or when the value is populated only after sync.
 */
import type { DentalChart } from "../schemas/dental.schema";


// ─── Sync metadata mixin ─────────────────────────────────────────────────────

export type SyncStatus = "pending" | "synced" | "error";

export interface SyncFields {
  sync_status: SyncStatus;
  version: number;
  local_updated_at: string;
}

// ─── Patients ────────────────────────────────────────────────────────────────

export interface PatientRecord extends SyncFields {
  patient_id: string;
  clinic_id?: string | null;
  patient_no?: number | null;
  display_id?: string | null;
  title?: string | null;
  first_name: string;
  middle_name?: string | null;
  last_name: string;
  date_of_birth: string;
  gender: "Male" | "Female" | "Other";
  blood_group?: string | null;
  marital_status?: string | null;
  occupation?: string | null;
  company?: string | null;
  contact_number: string;
  photo_s3_key?: string | null;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string | null;
  // Contact-table fields — stored locally so offline reads can populate all form fields.
  // None of these must ever appear in PLAINTEXT_FIELDS (aadhar_number is hard-enforced
  // by the encryption-bridge's aadhaar guard; the rest are PHI and must stay encrypted).
  email?: string | null;
  aadhar_number?: string | null;
  abha_id?: string | null;
  alternate_contact_number?: string | null;
  // Address-table fields — likewise encrypted at rest, not indexed.
  address?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
}

// ─── Visits ──────────────────────────────────────────────────────────────────

export type VisitStatus = "Draft" | "Finalized" | "Cancelled";

export interface VisitRecord extends SyncFields {
  visit_id: string;
  patient_id: string;
  doctor_id: string;
  visit_date: string;
  status: VisitStatus;
  chief_complaint?: string | null;
  history_present_illness?: string | null;
  physical_examination?: string | null;
  assessment?: string | null;
  plan?: string | null;
  note_soap?: string | null;
  notes?: string | null;
  lab_tests?: string[] | null;
  advice?: string | null;
  ai_generated?: boolean;
  follow_up_date?: string | null;
  dental_chart?: DentalChart | null;
  created_at?: string;
  updated_at?: string;
  finalized_at?: string | null;
  finalized_by?: string | null;
  sync_version?: number | null;
}

// ─── Vitals ──────────────────────────────────────────────────────────────────

export interface VitalsRecord extends SyncFields {
  vital_id: string;
  visit_id: string;
  blood_pressure_systolic?: number | null;
  blood_pressure_diastolic?: number | null;
  heart_rate?: number | null;
  respiratory_rate?: number | null;
  temperature?: number | null;
  temperature_unit?: "Celsius" | "Fahrenheit" | null;
  oxygen_saturation?: number | null;
  weight?: number | null;
  weight_unit?: "kg" | "lbs" | null;
  height?: number | null;
  height_unit?: "cm" | "inches" | null;
  bmi?: number | null;
  blood_sugar?: number | null;
  recorded_at?: string;
  recorded_by?: string | null;
}

// ─── Prescriptions ───────────────────────────────────────────────────────────

export type PrescriptionStatus = "Draft" | "Active" | "Discontinued";

export interface PrescriptionRecord extends SyncFields {
  prescription_id: string;
  visit_id: string;
  prescribed_by: string;
  prescribed_at?: string;
  status: PrescriptionStatus;
  valid_until?: string | null;
  pdf_s3_key?: string | null;
  warnings_flagged?: boolean;
  warning_details?: string | null;
  notes?: string | null;
  observation?: string | null;
  medications: MedicationEntry[];
  created_at?: string;
}

export interface MedicationEntry {
  medication_id: string;
  drug_id?: string | null;
  name: string;
  dosage: string;
  frequency: string;
  route?: string | null;
  duration?: string | null;
  quantity?: number | null;
  refills_allowed?: number;
  instructions?: string | null;
}

// ─── Appointments ────────────────────────────────────────────────────────────

export type AppointmentStatus = "Scheduled" | "Waiting" | "InRoom" | "Completed" | "Cancelled";

export interface AppointmentRecord extends SyncFields {
  appointment_id: string;
  patient_id: string;
  doctor_id: string;
  slot_id?: string | null;
  appointment_date: string;
  start_time: string;
  end_time?: string | null;
  status: AppointmentStatus;
  visit_id?: string | null;
  reason?: string | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

// ─── Allergies ───────────────────────────────────────────────────────────────

export type AllergySeverity = "Mild" | "Moderate" | "Severe" | "Life-Threatening";
export type AllergyReactionType = "Drug" | "Food" | "Environmental" | "Contact";

export interface AllergyRecord extends SyncFields {
  allergy_id: string;
  patient_id: string;
  allergen: string;
  severity: AllergySeverity;
  reaction_type?: AllergyReactionType | null;
  reaction?: string | null;
  verified_date?: string | null;
  notes?: string | null;
  created_at?: string;
  created_by?: string | null;
}

// ─── Drugs (read-only Drug Master cache) ─────────────────────────────────────

export interface DrugRecord extends SyncFields {
  drug_id: string;
  drug_name: string;
  generic_name: string;
  drug_type?: string | null;
  strength?: string | null;
  unit?: string | null;
  is_controlled_substance?: boolean;
}

// ─── Sync queue ──────────────────────────────────────────────────────────────

export type EntityType =
  | "patient"
  | "visit"
  | "vitals"
  | "prescription"
  | "appointment"
  | "allergy"
  | "doctor";

export type SyncOperation  = "create" | "update" | "delete";
export type SyncItemStatus = "pending" | "syncing" | "done" | "conflict" | "failed";

export interface SyncQueueRecord {
  id:               string;                     // idempotency key (client-assigned UUID)
  entity_type:      EntityType;                 // which table the mutation belongs to
  entity_id:        string | null;              // record PK; null for pending creates
  operation:        SyncOperation;              // create / update / delete
  data:             Record<string, unknown>;    // payload to apply
  version:          number;                     // local version at time of mutation (optimistic lock)
  status:           SyncItemStatus;             // queue lifecycle state
  retry_count:      number;                     // flush attempts so far
  last_error?:      string;                     // last server/transport failure reason
  device_id:        string;                     // source device (for server dedup + audit)
  client_timestamp: string;                     // ISO when the mutation was made locally
  server_snapshot?: Record<string, unknown>;    // server-side row at time of 409 — used by ConflictModal
}

// ─── On-disk shapes (Phase 3 — encryption-at-rest) ──────────────────────────
//
// IndexedDB rows hold a SLIM plaintext slice (primary key + the columns Dexie
// actually indexes + the sync-metadata mixin) plus an opaque `encrypted_data`
// string containing every other field.
//
// The in-memory record types above (PatientRecord, VisitRecord, …) stay
// unchanged — app code keeps working with the full record. The boundary that
// translates between in-memory and on-disk shapes lives in
// `./encryption-bridge.ts` and is wired in by `./operations.ts` and
// `../services/offline-sync.service.ts`. No call site outside that boundary
// should construct or consume an OnDisk row directly.
//
// Indexed plaintext columns leak whatever they index (e.g. patients.contact_number
// is queryable by phone — useful, but visible on disk). Documented v1 trade-off;
// move to deterministic-hash indexes in a follow-up if the threat model warrants.
//
// Drugs are NOT encrypted — public Drug Master cache, identical content on
// every device, no PHI. DrugOnDisk === DrugRecord by definition.

export type EncryptedBlob = string; // base64(IV || AES-GCM-ciphertext+tag)

interface EncryptedEnvelope {
  encrypted_data: EncryptedBlob;
}

// Each <Entity>OnDisk type below picks ONLY the plaintext-indexed fields plus
// the SyncFields mixin, then bolts on `encrypted_data`. Anything not picked
// rides inside the encrypted blob.

export type PatientOnDisk = SyncFields & EncryptedEnvelope & Pick<PatientRecord,
  "patient_id" | "clinic_id" | "contact_number" | "display_id">;

export type VisitOnDisk = SyncFields & EncryptedEnvelope & Pick<VisitRecord,
  "visit_id" | "patient_id" | "doctor_id" | "status" | "visit_date">;

export type VitalsOnDisk = SyncFields & EncryptedEnvelope & Pick<VitalsRecord,
  "vital_id" | "visit_id">;

export type PrescriptionOnDisk = SyncFields & EncryptedEnvelope & Pick<PrescriptionRecord,
  "prescription_id" | "visit_id" | "status">;

export type AppointmentOnDisk = SyncFields & EncryptedEnvelope & Pick<AppointmentRecord,
  "appointment_id" | "patient_id" | "doctor_id" | "appointment_date" | "status">;

export type AllergyOnDisk = SyncFields & EncryptedEnvelope & Pick<AllergyRecord,
  "allergy_id" | "patient_id" | "severity">;

export type DrugOnDisk = DrugRecord; // public reference data — not encrypted.

// Phase 2 dental plan item, pulled for offline visits. Only ids stay
// plaintext (for the indexes); tooth, procedure and status are clinical and
// live in the encrypted blob, like every other entity. Nothing writes this
// table yet: it ships empty one release ahead (see schema.ts v7).
export type TreatmentPlanItemOnDisk = SyncFields & EncryptedEnvelope & {
  id:         string;
  patient_id: string;
  plan_id:    string;
};

// SyncQueue replaces its `data: Record<...>` with a single encrypted blob.
// Lifecycle/audit fields (status, retry_count, device_id, client_timestamp,
// entity_type, entity_id) all stay plaintext so queue sweeps + retries work.
export type SyncQueueOnDisk = Omit<SyncQueueRecord, "data"> & EncryptedEnvelope;

// ─── Encryption schema (which fields stay plaintext per entity) ─────────────
//
// Source of truth for `encryption-bridge.ts`. Add an entry here whenever a new
// entity becomes offline-writable. The mixin sync fields are added implicitly
// by the bridge — list ONLY the entity-specific plaintext columns.
//
// IMPORTANT: aadhaar_number / aadhar_number must NEVER appear in any list
// here. The bridge enforces this with a runtime assertion, but defence in
// depth — eyes on this list at review time too.

export const PLAINTEXT_FIELDS: Readonly<Record<EntityType, readonly string[]>> = {
  patient:      ["patient_id", "clinic_id", "contact_number", "display_id"],
  visit:        ["visit_id", "patient_id", "doctor_id", "status", "visit_date"],
  vitals:       ["vital_id", "visit_id"],
  prescription: ["prescription_id", "visit_id", "status"],
  appointment:  ["appointment_id", "patient_id", "doctor_id", "appointment_date", "status"],
  allergy:      ["allergy_id", "patient_id", "severity"],
  // doctors have no local Dexie table — managed server-side only; no plaintext fields to index
  doctor:       [],
} as const;

export const SYNC_FIELDS_KEYS = ["sync_status", "version", "local_updated_at"] as const;
